import { createProductVendor } from '../../../../store/productVendors/actions';

const normalizeVendorTitle = (title) => {
  if (!title) return '';
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
};

/**
 * Find a registry item by case/whitespace-insensitive title match.
 * @param {Array<{_id: string, title: string}>} items
 * @param {string} title
 * @returns {{_id: string, title: string}|null}
 */
export const findVendorByNormalizedTitle = (items, title) => {
  const normalizedTitle = normalizeVendorTitle(title);
  if (!normalizedTitle) return null;

  return items.find(
    (vendor) => normalizeVendorTitle(vendor.title) === normalizedTitle,
  ) || null;
};

/**
 * Resolve the "checked" dropdown item(s) for the current field value.
 * - No value selected -> nothing checked.
 * - Value matches a currently loaded vendor -> that registry item is checked. Unlike product
 *   types, the vendor registry is normalized/deduped server-side, so a simple case-insensitive
 *   match is sufficient (no case-duplicate ambiguity to resolve).
 * - Value has no match in the currently loaded items -> a synthetic placeholder item is checked
 *   so the dropdown still displays the current value. This also covers a legacy/live vendor that
 *   lives on a page the paginated registry hasn't fetched yet.
 * @param {Array<{_id: string, title: string}>} items
 * @param {string} value
 * @returns {Array<{_id: string, title: string}>}
 */
export const getCheckedVendorItems = (items, value) => {
  if (!value) return [];

  const exactMatch = items.find((vendor) => vendor.title === value);
  const matchedVendor = exactMatch || findVendorByNormalizedTitle(items, value);
  if (matchedVendor) return [matchedVendor];

  return [{ _id: `__unregistered_vendor_${value}`, title: value }];
};

/**
 * Decide whether a `dispatch(createProductVendor(...))` result should be applied to the
 * product's `vendor` field. A tolerated "already exists" (409) case resolves the thunk as
 * fulfilled just like a fresh create — the title is valid either way. A genuine failure
 * (network/5xx/permission) resolves the thunk as rejected and must never be silently applied.
 * @param {{type: string}} result - the action returned by `dispatch(createProductVendor(...))`
 * @returns {boolean}
 */
export const shouldApplyVendorCreateResult = (result) => createProductVendor.fulfilled.match(result);

export default getCheckedVendorItems;
